import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Calendar, Tag, Share2, Twitter, Linkedin, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

const fadeUp = {
 initial: { opacity: 0, y: 20 },
 animate: { opacity: 1, y: 0 },
 transition: { duration: 0.5 },
};

const categoryLabels = {
 validation: "Validation",
 product: "Product",
 founder: "Founder Mindset",
 scaling: "Scaling",
};

// Fallback static content for when API has no data
const fallbackPostsContent = {
 "1": {
  title: "Why Most MVPs Fail (And What to Build Instead)",
  excerpt: "The problem isn't your MVP. It's that you built before you validated. Here's how to flip the script.",
  category: "validation",
  read_time: "5 min",
  created_at: "2024-01-15",
  author_name: "Let's Build Startup",
  content: `
   <p>Every founder has heard the mantra: "Ship fast, iterate faster." And it's not wrong. speed matters. But there's a critical step that most founders skip in their rush to ship: <strong>validating the problem before building the solution</strong>.</p>

   <h2>The MVP Trap</h2>
   <p>The concept of a Minimum Viable Product was revolutionary when Eric Ries introduced it. But somewhere along the way, founders started interpreting "minimum viable" as "build the smallest thing possible and see if anyone cares."</p>
   <p>Here's the problem: by the time you've built something. even a small something. you've already invested time, money, and emotional energy. You're no longer objective. You're looking for validation, not truth.</p>

   <h2>What Happens Next</h2>
   <p>You launch your MVP to crickets. Or worse, you get some initial interest that quickly fades. You tell yourself it's a distribution problem, or a messaging problem, or a timing problem. So you iterate. You add features. You pivot slightly. You raise some money to give yourself more runway.</p>
   <p>Six months later, you've built a product that nobody really wants, and you've run out of both money and excuses.</p>

   <h2>The Alternative: Validate Before You Build</h2>
   <p>What if, instead of building an MVP, you spent 3-4 weeks validating the problem itself? What if you talked to 50 potential customers before writing a single line of code? What if you pre-sold your solution before you built it?</p>
   <p>This isn't theoretical. This is what successful founders actually do.</p>

   <blockquote>"The biggest risk isn't building the wrong features. it's building the wrong product entirely."</blockquote>

   <h2>A Better Framework</h2>
   <ol>
    <li><strong>Problem Validation</strong>: Talk to 20-50 potential customers. Don't pitch. listen. Understand their pain points, their current solutions, and what they'd pay to solve the problem.</li>
    <li><strong>Solution Validation</strong>: Before building, describe your solution and gauge genuine interest. Would they pay for it? How much? When?</li>
    <li><strong>Demand Validation</strong>: Pre-sell or get commitments before building. If no one will commit before the product exists, they probably won't commit after either.</li>
    <li><strong>Then Build</strong>: Only after you have validated demand should you build your MVP.</li>
   </ol>

   <h2>The Bottom Line</h2>
   <p>The goal isn't to ship fast. it's to learn fast. And you can learn a lot faster by talking to customers than by building features they might not want.</p>
   <p>Stop building MVPs that no one asked for. Start validating problems before you invest in solutions.</p>
  `,
 },
 "2": {
  title: "The Real Cost of Building the Wrong Thing",
  excerpt: "We've watched founders spend $100k+ on products nobody wanted. Here's how to avoid that trap.",
  category: "validation",
  read_time: "7 min",
  created_at: "2024-01-10",
  author_name: "Let's Build Startup",
  content: `
   <p>In our years of working with founders, we've seen a pattern repeat itself over and over: smart, capable people building products that nobody wants to buy. It's not a failure of execution. it's a failure of validation.</p>

   <h2>The Hidden Costs</h2>
   <p>When founders talk about the cost of building the wrong thing, they usually think about money. And yes, the financial cost is real. We've seen founders burn through $100k, $200k, sometimes more, building products that never find a market.</p>
   <p>But the real costs go deeper:</p>
   <ul>
    <li><strong>Time</strong>: 12-18 months of your life, gone. That's time you could have spent finding the right problem to solve.</li>
    <li><strong>Opportunity cost</strong>: Every day you spend building the wrong thing is a day you're not building the right thing.</li>
    <li><strong>Emotional toll</strong>: The psychological weight of failure is heavy. Many founders never start another company after a failed venture.</li>
    <li><strong>Reputation</strong>: Your investors, your team, your network. they all remember. Future opportunities become harder to access.</li>
   </ul>

   <h2>Why It Happens</h2>
   <p>Building the wrong thing usually stems from a few common mistakes:</p>
   <ul>
    <li><strong>Falling in love with your idea</strong>: You become so attached to your vision that you stop listening to what the market is telling you.</li>
    <li><strong>Mistaking interest for demand</strong>: "That sounds cool" is not the same as "I'll pay for that."</li>
    <li><strong>Building for yourself</strong>: Just because you have a problem doesn't mean enough other people do.</li>
    <li><strong>Ignoring early warning signs</strong>: When customers don't convert, founders blame marketing instead of questioning product-market fit.</li>
   </ul>

   <h2>The Antidote</h2>
   <p>The antidote is simple but requires discipline: validate before you build.</p>
   <p>Talk to customers. Not friends and family. real potential customers who would actually pay for what you're building. Listen more than you talk. Look for patterns in their pain points. Test your assumptions before you invest in them.</p>

   <blockquote>"The cheapest experiment you can run is a conversation. The most expensive experiment is a product no one wants."</blockquote>

   <h2>A Framework for Avoiding the Trap</h2>
   <ol>
    <li>Before building anything, talk to 30+ potential customers</li>
    <li>Get at least 5 people to commit to paying before you write code</li>
    <li>Set kill criteria: if X doesn't happen by Y date, pivot or stop</li>
    <li>Review your assumptions monthly. what have you learned?</li>
   </ol>

   <p>Building the wrong thing isn't inevitable. It's a choice. Choose differently.</p>
  `,
 },
 "3": {
  title: "How to Know If Your Idea Is Worth Pursuing",
  excerpt: "Passion isn't enough. Here's a practical framework for evaluating whether your idea has legs.",
  category: "validation",
  read_time: "6 min",
  created_at: "2024-01-05",
  author_name: "Let's Build Startup",
  content: `
   <p>Every founder starts with conviction. You see a problem, you have a solution, and you believe. deeply. that you're onto something. But conviction isn't validation. And the graveyard of startups is full of founders who were passionate about ideas that weren't worth pursuing.</p>

   <h2>The Idea Evaluation Framework</h2>
   <p>Here's a practical framework we use with founders to evaluate whether an idea is worth pursuing:</p>

   <h3>1. Is the problem real?</h3>
   <p>Not "could this be a problem" or "wouldn't it be nice if". is this a problem that people actively experience and actively seek solutions for?</p>

   <h3>2. Is the problem painful enough?</h3>
   <p>There are problems, and then there are problems people will pay to solve. The difference is pain intensity.</p>

   <h3>3. Is the market big enough?</h3>
   <p>A real, painful problem in a tiny market won't build a sustainable business.</p>

   <blockquote>"An idea isn't a business. A validated problem with a viable solution and a reachable market. that's a business."</blockquote>

   <h2>The Pass/Fail Test</h2>
   <p>After evaluating an idea against these criteria, you should have a clear sense of whether to proceed. If you can't answer "yes" to most of these questions with evidence (not just belief), the idea probably isn't ready.</p>
   <p>The key is to let evidence, not passion, drive your decision.</p>
  `,
 },
 "4": {
  title: "Pricing Validation: How to Know What People Will Actually Pay",
  excerpt: "Stop guessing. Here's how to validate your pricing before you build anything.",
  category: "product",
  read_time: "8 min",
  created_at: "2023-12-28",
  author_name: "Let's Build Startup",
  content: `
   <p>Pricing is one of the most uncomfortable topics for founders. We obsess over product features and market positioning, but when it comes to pricing, most founders just... guess. And then they underprice, leaving money on the table while struggling to build a sustainable business.</p>

   <h2>Why Pricing Matters More Than You Think</h2>
   <p>A 10% improvement in pricing typically has a larger impact on profitability than a 10% improvement in customer acquisition or retention. Pricing is a lever. and most founders don't pull it hard enough.</p>

   <h2>The Van Westendorp Method</h2>
   <p>One effective way to validate pricing is the Van Westendorp Price Sensitivity Meter.</p>

   <h2>Test Actual Behavior</h2>
   <p>The most reliable pricing validation comes from actual buying behavior. This is why pre-selling is so powerful. it tests both demand and price sensitivity at once.</p>

   <blockquote>"Customers don't know what they'll pay until they have to pay it. Test with real transactions whenever possible."</blockquote>
  `,
 },
 "5": {
  title: "The Customer Conversation Framework That Actually Works",
  excerpt: "Most founder interviews are useless. Here's how to have conversations that reveal real insights.",
  category: "validation",
  read_time: "9 min",
  created_at: "2023-12-20",
  author_name: "Let's Build Startup",
  content: `
   <p>Every startup advisor tells founders to "talk to customers." But most founder interviews are useless. You ask leading questions, customers tell you what you want to hear, and you walk away with false confidence in a product nobody actually wants.</p>

   <h2>The Problem with Most Customer Interviews</h2>
   <p>The typical founder interview goes something like this:</p>
   <p>"We're building a tool that does X, Y, and Z. Would you use something like this?"</p>

   <h2>The Mom Test</h2>
   <p>Rob Fitzpatrick's "The Mom Test" offers a better framework. The core idea: ask questions that even your mom can't lie to you about.</p>

   <blockquote>"The goal of a customer interview isn't to feel good about your idea. It's to learn something that might change your mind."</blockquote>
  `,
 },
 "6": {
  title: "When to Pivot (And When to Persist)",
  excerpt: "The hardest founder decision: knowing when to keep going vs. when to change direction.",
  category: "founder",
  read_time: "6 min",
  created_at: "2023-12-15",
  author_name: "Let's Build Startup",
  content: `
   <p>Every founder faces this moment: things aren't working as expected, and you have to decide. do you push through, or do you change direction?</p>

   <h2>Signs It's Time to Pivot</h2>
   <ul>
    <li><strong>Consistent rejection from target customers</strong></li>
    <li><strong>You've proven your initial hypothesis wrong</strong></li>
    <li><strong>You're not excited anymore</strong></li>
   </ul>

   <h2>Signs You Should Persist</h2>
   <ul>
    <li><strong>Some customers love it</strong></li>
    <li><strong>You're still learning fast</strong></li>
   </ul>

   <blockquote>"A pivot isn't giving up. It's taking what you've learned and applying it to a better opportunity."</blockquote>
  `,
 },
};

export default function BlogPostPage() {
 const { id } = useParams();
 const navigate = useNavigate();
 const [post, setPost] = useState(null);
 const [relatedPosts, setRelatedPosts] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState(null);

 const setFallbackRelatedPosts = useCallback((category, currentId) => {
  const related = Object.entries(fallbackPostsContent)
   .filter(([postId, p]) => postId !== currentId && p.category === category)
   .slice(0, 3)
   .map(([postId, p]) => ({ ...p, id: postId }));
  setRelatedPosts(related);
 }, []);

 const fetchRelatedPosts = useCallback(async (category, currentId) => {
  try {
   const response = await fetch(`${API_URL}/api/blogs/public?category=${category}&limit=4`);
   
   if (response.ok) {
    const data = await response.json();
    const filtered = data.filter(p => p.id !== currentId).slice(0, 3);
    if (filtered.length > 0) {
     setRelatedPosts(filtered);
    } else {
     setFallbackRelatedPosts(category, currentId);
    }
   } else {
    setFallbackRelatedPosts(category, currentId);
   }
  } catch {
   setFallbackRelatedPosts(category, currentId);
  }
 }, [setFallbackRelatedPosts]);

 const fetchPost = useCallback(async () => {
  setLoading(true);
  setError(null);
  
  try {
   const response = await fetch(`${API_URL}/api/blogs/public/${id}`);
   
   if (response.ok) {
    const data = await response.json();
    setPost(data);
    // Fetch related posts after getting the main post
    fetchRelatedPosts(data.category, data.id);
   } else if (response.status === 404) {
    // Try fallback content
    const fallback = fallbackPostsContent[id];
    if (fallback) {
     setPost({ ...fallback, id });
     fetchRelatedPosts(fallback.category, id);
    } else {
     setError("Post not found");
    }
   } else {
    throw new Error("Failed to fetch blog post");
   }
  } catch (err) {
   console.error("Error fetching blog post:", err);
   // Try fallback content on error
   const fallback = fallbackPostsContent[id];
   if (fallback) {
    setPost({ ...fallback, id });
    fetchRelatedPosts(fallback.category, id);
   } else {
    setError("Failed to load blog post");
   }
  } finally {
   setLoading(false);
  }
 }, [id, fetchRelatedPosts]);

 useEffect(() => {
  fetchPost();
 }, [fetchPost]);

 const formatDate = (dateString) => {
  try {
   return new Date(dateString).toLocaleDateString('en-US', { 
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
   });
  } catch {
   return dateString;
  }
 };

 if (loading) {
  return (
   <div className="flex flex-col items-center justify-center min-h-[60vh]">
    <Loader2 className="h-8 w-8 text-accent animate-spin mb-4" />
    <p className="text-muted-foreground">Loading post...</p>
   </div>
  );
 }

 if (error || !post) {
  return (
   <div className="flex flex-col items-center justify-center min-h-[60vh]">
    <h1 className="heading-section text-foreground mb-4">Post Not Found</h1>
    <p className="text-muted-foreground mb-6">The blog post you're looking for doesn't exist.</p>
    <Button variant="accent" asChild>
     <Link to="/blog" data-testid="back-to-blog-link">Back to Blog</Link>
    </Button>
   </div>
  );
 }

 return (
  <div className="flex flex-col">
   {/* Hero */}
   <section className="bg-hero section-padding-sm">
    <div className="container-narrow">
     <motion.div
      initial="initial"
      animate="animate"
      variants={fadeUp}
     >
      <Button
       variant="ghost"
       size="sm"
       onClick={() => navigate(-1)}
       className="mb-8 -ml-2"
       data-testid="back-to-blog-btn"
      >
       <ArrowLeft className="h-4 w-4 mr-2" />
       Back to Blog
      </Button>

      <div className="flex items-center gap-3 mb-4">
       <Badge variant="outline">{categoryLabels[post.category] || post.category}</Badge>
       <span className="text-sm text-muted-foreground flex items-center gap-1">
        <Clock className="h-3.5 w-3.5" />
        {post.read_time || "5 min"} read
       </span>
       <span className="text-sm text-muted-foreground flex items-center gap-1">
        <Calendar className="h-3.5 w-3.5" />
        {formatDate(post.published_at || post.created_at)}
       </span>
      </div>

      <h1 className="heading-large text-foreground mb-6" data-testid="blog-post-title">
       {post.title}
      </h1>

      <p className="body-large text-muted-foreground mb-8">
       {post.excerpt}
      </p>

      <div className="flex items-center justify-between pt-6 border-t border-border/50">
       <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
         <span className="font-serif text-accent font-medium">G</span>
        </div>
        <div>
         <p className="text-sm font-medium text-foreground">{post.author_name || "Let's Build Startup"}</p>
         <p className="text-xs text-muted-foreground">Founder-to-Founder Insights</p>
        </div>
       </div>

       <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="share-twitter-btn">
         <Twitter className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="share-linkedin-btn">
         <Linkedin className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="share-btn">
         <Share2 className="h-4 w-4" />
        </Button>
       </div>
      </div>
     </motion.div>
    </div>
   </section>

   {/* Content */}
   <section className="bg-background section-padding">
    <div className="container-narrow">
     <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="prose prose-lg prose-slate max-w-none
       prose-headings:font-serif prose-headings:font-medium prose-headings:text-foreground
       prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4
       prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
       prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:mb-6
       prose-strong:text-foreground prose-strong:font-medium
       prose-ul:text-muted-foreground prose-ul:my-4
       prose-ol:text-muted-foreground prose-ol:my-4
       prose-li:mb-2
       prose-blockquote:border-l-4 prose-blockquote:border-accent prose-blockquote:pl-6 prose-blockquote:py-2 prose-blockquote:my-8
       prose-blockquote:text-xl prose-blockquote:font-serif prose-blockquote:italic prose-blockquote:text-foreground
      "
      data-testid="blog-post-content"
      dangerouslySetInnerHTML={{ __html: post.content }}
     />
    </div>
   </section>

   {/* Author CTA */}
   <section className="bg-section py-16">
    <div className="container-narrow">
     <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="flex flex-col md:flex-row items-center gap-6 md:gap-8"
     >
      <div className="w-20 h-20 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
       <span className="font-serif text-2xl text-accent font-medium">G</span>
      </div>
      <div className="text-center md:text-left flex-1">
       <h3 className="font-serif text-xl font-medium text-foreground mb-2">Written by Let's Build Startup</h3>
       <p className="text-muted-foreground">
        We help early-stage and growth-stage founders move from idea to clarity to execution. 
        These insights come from years of working with founders in the trenches.
       </p>
      </div>
      <Button variant="accent" asChild className="rounded-full flex-shrink-0">
       <Link to="/contact">Work With Us</Link>
      </Button>
     </motion.div>
    </div>
   </section>

   {/* Related Posts */}
   {relatedPosts.length > 0 && (
    <section className="bg-background section-padding-sm">
     <div className="container-base">
      <h2 className="heading-section text-foreground mb-8 text-center">Related Articles</h2>
      <div className="grid md:grid-cols-3 gap-6">
       {relatedPosts.map((relatedPost, i) => (
        <motion.div
         key={relatedPost.id}
         initial={{ opacity: 0, y: 20 }}
         whileInView={{ opacity: 1, y: 0 }}
         viewport={{ once: true }}
         transition={{ duration: 0.5, delay: i * 0.1 }}
        >
         <Card 
          className="card-elevated h-full flex flex-col group cursor-pointer"
          onClick={() => navigate(`/blog/${relatedPost.id}`)}
          data-testid={`related-post-${relatedPost.id}`}
         >
          <CardContent className="p-6 flex flex-col flex-1">
           <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline" className="text-xs">
             {categoryLabels[relatedPost.category] || relatedPost.category}
            </Badge>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
             <Clock className="h-3 w-3" />
             {relatedPost.read_time || "5 min"}
            </span>
           </div>
           
           <h3 className="font-serif text-lg font-medium text-foreground mb-2 group-hover:text-accent transition-colors">
            {relatedPost.title}
           </h3>
           
           <p className="text-sm text-muted-foreground flex-1">
            {relatedPost.excerpt}
           </p>

           <div className="pt-4 mt-4 border-t border-border/50">
            <span className="text-sm font-medium text-accent flex items-center gap-1">
             Read article
             <ArrowRight className="h-3 w-3" />
            </span>
           </div>
          </CardContent>
         </Card>
        </motion.div>
       ))}
      </div>
     </div>
    </section>
   )}

   {/* Final CTA */}
   <section className="bg-section section-padding-sm">
    <div className="container-narrow text-center">
     <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
     >
      <h2 className="heading-section text-foreground mb-4">Ready to Move From Idea to Clarity?</h2>
      <p className="body-base text-muted-foreground mb-6 max-w-xl mx-auto">
       We help founders validate faster, build smarter, and scale sustainably.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
       <Button variant="accent" size="lg" asChild className="rounded-full">
        <Link to="/contact" data-testid="cta-submit-wishlist">
         Submit Your Wishlist
         <ArrowRight className="h-4 w-4 ml-1" />
        </Link>
       </Button>
       <Button variant="outline" size="lg" asChild className="rounded-full">
        <Link to="/blog" data-testid="cta-more-articles">
         More Articles
        </Link>
       </Button>
      </div>
     </motion.div>
    </div>
   </section>
  </div>
 );
}
